/**
 * THE QUESTIONS THAT SHARPEN THE ANSWER.
 *
 * The checkup tells a Nigerian mini-grid developer: 1 licence required, 4 conditional, 28 under
 * review. The "4 conditional" are not vague — the corpus knows EXACTLY what each one turns on. It
 * simply was never asked:
 *
 *   SONCAP Certificate             imports_equipment        is true
 *   NCS Import Clearance           imports_equipment        is true
 *   Battery PRO Registration       has_battery_storage      is true
 *   DARES Programme Qualification  programme_participation  includes "DARES"
 *
 * Three facts. Four licences resolved. And "you do NOT need SONCAP" is worth every bit as much to
 * a developer as "you do" — today the checkup can say neither.
 *
 * THESE ARE NOT MY QUESTIONS. Every one is read straight back off a condition the regulatory
 * corpus authored (`license_type.applies_when`). I am not deciding what binds a project; I am
 * asking the question the corpus says it needs in order to decide. If the corpus changes its
 * conditions, this list should change with it — see checkup-questions.test.ts, which fails if a
 * conditional licence appears whose deciding fact nobody asks about.
 *
 * Answering is optional, always. An unanswered question leaves the licence conditional — shown
 * and flagged, never assumed in either direction.
 */

import type { ProjectAttribute } from "./applicability.ts";

/** The attribute keys evaluateCheckup() will read out of `fields`. An allowlist: a key not on it
 *  is ignored, so a typo can never widen what the engine believes about someone's project.
 *  `satisfies` makes the other direction a compile error too: a key that applicability.ts does
 *  not understand cannot be added here. */
export const ATTRIBUTE_KEYS = [
  "imports_equipment",
  "has_battery_storage",
  "programme_participation",
] as const satisfies readonly ProjectAttribute[];

export type CheckupAttribute = (typeof ATTRIBUTE_KEYS)[number];

export interface CheckupQuestion {
  /** The ProjectAttributes key — derived from ATTRIBUTE_KEYS so the two cannot drift. */
  attribute: CheckupAttribute;
  /** Asked in the developer's language, not the corpus's. */
  question: string;
  /** Why it is worth answering — what it resolves. Shown under the question. */
  why: string;
  /** The value written when they say yes / no. Strings, because that is what the API carries. */
  yes: string;
  no: string;
}

/** The sentinel for "I take part in no programme". Distinct from the empty string, which means
 *  "never asked" and correctly leaves the licence conditional. */
export const NO_PROGRAMME = "__none__";

export const CHECKUP_QUESTIONS: CheckupQuestion[] = [
  {
    attribute: "imports_equipment",
    question: "Will you import any equipment for this project?",
    why: "Panels, inverters, batteries, transformers — anything shipped in. Decides two licences.",
    yes: "true",
    no: "false",
  },
  {
    attribute: "has_battery_storage",
    question: "Does the system include battery storage?",
    why: "Batteries must be registered for end-of-life collection and disposal — a separate registration for whoever brings them in.",
    yes: "true",
    no: "false",
  },
  {
    attribute: "programme_participation",
    question: "Are you taking part in the DARES programme?",
    why: "The World Bank–backed rural electrification programme. If you're not, one requirement drops away.",
    yes: "DARES",
    // NOT the empty string. "I am in no programme" is an ANSWER and must rule the DARES row OUT;
    // the empty string is indistinguishable from never having been asked, and evaluateCheckup
    // correctly treats "not asked" as "stays conditional". So a "no" needs a value of its own.
    // (I shipped `no: ""` first, and it silently did nothing — the developer answered and the
    // number didn't move.)
    no: NO_PROGRAMME,
  },
  // imports_generator / employs_expatriates / local_assembly_or_manufacture
  // questions were drafted here and REMOVED before merge: their seed
  // conditions in the corpus match instruments still pending acquisition
  // (NERC generator clearance, MANCAP, expatriate quota), so no catalogue
  // row conditions on them yet — a question whose answer changes nothing is
  // dead UI. The conformance gate now enforces this in the FORWARD direction
  // (every question must have a consumer); re-add each question in the same
  // PR as the dataset that grounds its row.
];
